import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, AfterViewInit, Input, Inject, Optional, Output, EventEmitter } from '@angular/core';

import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { switchMap, map, tap } from 'rxjs/operators';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { TableColumnModel } from '../../../Forms/models/table-column.model';
import { TableColumnInputEnum } from '../../../Forms/enums/table-column-input-type.enum';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { TargetingListList } from '../../../../Discover/Targeting/models/targeting-list-list.model';
import { TargetingResultModel } from '../../../../Discover/Targeting/models/targeting-result.model';
import { ChipModel } from '../../../Forms/models/chip.model';
import { TextInputDialogModel } from '../../../Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '../../../Dialogs/components/TextInputDialog/text-input-dialog.component';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { DateHelper } from '../../../Forms/helpers/date.helper';
import { AutocompleteCustomOption, AppearCondition } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ListPickerService } from '../../services/list-picker.service';
import { TargetingList } from '../../../../Discover/Targeting/models/targeting-list.model';
import { CommonApiErrorEnum } from '../../../../api/genericApi/enums/common-api-error.enum';


export class DataRow {
    id?: number;
    name?: string;
    count?: number;
    selected?: boolean;
    originalData: TargetingListList;
    createdUser?: string;
    createdDate?: string; // Formatted
    modifiedUser?: string;
    modifiedDate?: string; // Formatted
}

@Component({
    selector: 'agpr-list-picker-dialog',
    templateUrl: './list-picker-dialog.component.html',
    styleUrls: ['./list-picker-dialog.component.scss'],
    providers: [
        {
            // has a fresh context for adding from anywhere
            // TODO : make it not create this if a context service is provided
            provide: 'AdHocListPickerService',
            useClass: ListPickerService
        }
    ],
    standalone: false
})
export class ListPickerDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() addMode: boolean = true;
    @Input() addImmediately: boolean = true;
    @Input() showListBuilderCheckbox = true;
    @Input() addHocAddEntity: TargetingResultModel;
    @Input() outletUuid: string = null;

    @Input() set initialSelectedLists(lists: TargetingListList[]) {
        this.listPickerService.setWorkingLists(lists);
    }

    @Output() listMembershipChanged: EventEmitter<void> = new EventEmitter<void>();
    @Output() newSelectedLists: EventEmitter<TargetingListList[]> = new EventEmitter<TargetingListList[]>();

    @ViewChild('countInfoCell') public countCellTemplate: TemplateRef<any>;

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;

    subscriptions: Subscription[] = [];

    id = 'listPickerDialog';
    header: string = 'Discover.listBuilder.dialog.header';
    loading: boolean = false;

    dataRows: DataRow[];
    filteredRows: DataRow[] = [];
    freshCreatedId: number;
    workingListsChips: ChipModel[] = [];

    addToListBuilder = false;
    listPickerService: ListPickerService;

    selectedEntities: TargetingResultModel[];
    selectedCount: number = 0;

    nameColumn: TableColumnModel = {
        type: TableColumnInputEnum.text,
        heading: 'Discover.listBuilder.table.listname',
        field: 'name',
        tooltipField: 'name',
        sortable: true,
        sortType: 'text',
        selectable: true,
        selectField: 'selected',
        width: 300
    };

    countColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'Discover.listBuilder.table.contacts',
        field: 'count',
        width: 150,
        sortable: true,
    };

    columnDefs: TableColumnModel[] = [];

    listsAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Discover.listBuilder.createList',
            action: (listName: string) => {
                if (listName.length === 0) {

                    return;
                }

                this.loading = true;

                this.targetingListService.isNameDuplicated$(listName).pipe(
                    switchMap((duplicate) => {
                        if (duplicate) {
                            throw new Error(CommonApiErrorEnum.duplicateName);
                        } else {
                            const list = new TargetingList({name: listName});
                            return this.targetingListService.create(list);
                        }
                    })
                ).subscribe({
                    next: (id) => {
                        this.freshCreatedId = id;
                        this.loading = false;
                        this.newlyAddedList();
                    },
                    error: () => {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Discover.dialogs.newList.duplicate',
                        });
                        this.resetLists();
                        this.loading = false;
                    }
                });
            },
            appearCondition: this.filteredRows.length > 0 ? null : AppearCondition.always,
            clearOnSelect: false
        }
    ];

    constructor(
        public coreDialogService: CoreDialogService,
        public targetingListService: TargetingListService,
        public usersService: UsersService,
        private messageService: MessageService,
        @Optional() public contextListPickerService: ListPickerService,
        @Inject('AdHocListPickerService') adHocListPickerService: ListPickerService
    ) {
        super(coreDialogService, 'ListPicker');

        this.listPickerService = !!contextListPickerService ? contextListPickerService : adHocListPickerService;

        this.subscriptions.push(
            this.targetingListService.all$.subscribe(lists => {
                if (!lists) {
                    return;
                }
                this.dataRows = lists.map(list => {
                    return {
                        id: list.id,
                        selected: !!this.workingListsChips.find(x => x.label === list.name),
                        name: list.name,
                        count: list.count,
                        originalData: list,
                        createdDate: DateHelper.toDateString(list.createdDate),
                        modifiedDate: DateHelper.toDateString(list.modifiedDate),
                    };
                });
                this.filteredRows = this.dataRows;

                this.fetchUserNames();
                this.newlyAddedList();
            }),
            this.listPickerService.getWorkingLists().subscribe(lists => {
                this.workingListsChips = lists.map(x => ({ label: x.name }));
                this.populateDialogFromInitialSelection();
            }),
            this.listPickerService.getSelectedEntities().subscribe(entities => {
                this.selectedEntities = entities;
            }),
            this.listPickerService.getTotal().subscribe(total => {
                this.selectedCount = total;
            })
        );
    }

    ngOnInit() {
        if (!this.addMode) {
            this.header = 'Discover.listBuilder.dialog.selectMode.header';
        }
        if (this.addHocAddEntity) {
            this.listPickerService.setSelectedEntities([this.addHocAddEntity]);
        }
    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.countColumn.customTemplate = this.countCellTemplate;
            this.columnDefs = [
                this.nameColumn,
                this.countColumn
            ];
        });
    }

    newlyAddedList() {
        if (this.freshCreatedId) {
            const freshCreatedRow = this.dataRows.find(x => x.id === this.freshCreatedId);
            freshCreatedRow.selected = true;
            this.workingListsChips.push({
                label : freshCreatedRow.name
            });
            this.freshCreatedId = undefined;
        }
    }

    private populateDialogFromInitialSelection() {
        if (this.dataRows && this.workingListsChips.length > 0) {
            this.workingListsChips.forEach(workingList => {
                const list = this.dataRows.find(x => x.name === workingList.label);
                if (list) {
                    list.selected = true;
                }
            });
        }
    }

    /** Used when addMode set to false, emitting selected lists */
    selectLists() {
        const selectedLists: TargetingListList[] = this.dataRows
            .filter(x => x.selected)
            .map(x => x.originalData);

        this.listPickerService.setWorkingLists(selectedLists);
        this.newSelectedLists.emit(selectedLists);
        this.closeDialog();
    }

    /** Used when addMode set to true, adding contacts to lists depending on addImmediately flag */
    updateLists() {
        this.loading = true;
        let addComplete: Promise<void>;

        const selectedLists: TargetingListList[] = this.dataRows
            .filter(x => x.selected)
            .map(x => x.originalData);

        if (!this.addImmediately) {
            this.newSelectedLists.emit(selectedLists);
            this.closeDialog();
            return;
        }

        if (this.addToListBuilder) {
            this.listPickerService.setWorkingLists(selectedLists);
            this.listPickerService.setWorkingListsLock(true);
            addComplete = this.listPickerService.addSelectedEntitiesToWorkingLists(this.outletUuid);
        } else {
            addComplete = this.listPickerService.addSelectedEntitiesToLists(selectedLists, this.outletUuid);
            if (this.addHocAddEntity) {
                addComplete = addComplete.then(done => this.listMembershipChanged.emit());
            }
        }

        addComplete.then(
            done => {
                this.loading = false;
                this.closeDialog();
            },
            failed => {
                this.loading = false;
                this.closeDialog();
            });
    }

    selectionChanged(item: DataRow) {
        if (item.selected) {
            this.workingListsChips.push(<ChipModel>{ label: item.name });
        } else {
            this.workingListsChips.splice(this.workingListsChips.findIndex(x => x.label === item.name), 1);
        }
        this.workingListsChips = this.workingListsChips.slice();
    }

    selectAllChanged(allSelected: boolean) {
        this.workingListsChips = [];

        if (allSelected) {
            this.dataRows.forEach(row => {
                this.workingListsChips.push(<ChipModel>{ label: row.name });
            });
        }
    }

    removeChip(index: number) {
        const chipName = this.workingListsChips[index]?.label;
        this.workingListsChips.splice(index, 1);
        this.dataRows.find(x => x.name === chipName).selected = false;
    }

    resetLists() {
        this.filteredRows = this.dataRows;
    }

    filterRows(searchString: string) {
        if (searchString.length === 0) {
            this.filteredRows = this.dataRows;
        }
        this.filteredRows = this.dataRows.filter(row => row.name.toLowerCase().indexOf(searchString.toLowerCase()) > -1);
        this.listsAutocompleteOptions.find(x => x.label === 'Discover.listBuilder.createList').appearCondition
            = this.filteredRows.length > 0 ? null : AppearCondition.always;
    }

    createNewList() {
        // TODO : Can maybe remove some duplication with the left sidebar version of this dialog
        const io: TextInputDialogModel = {
            inputs: {
                heading: 'Discover.dialogs.newList.heading',
                description: 'Discover.dialogs.newList.description',
                placeholder: 'Discover.dialogs.newList.placeholder',
                okLabel: 'Discover.dialogs.newList.createList',
                cancelLabel: 'Discover.dialogs.universal.cancelLabel',
                width: 300,
                disabledButtonTooltip: 'Discover.dialogs.newList.listNameRequired',
                submit: (listName: string) => {
                    const newList = new TargetingList({name: listName});
                    return this.targetingListService.create(newList).pipe(
                        tap(id => {
                            this.freshCreatedId = id;
                            this.newlyAddedList();
                        })
                    );
                },
                observableFormValidator: (listName: string) => {
                    return this.targetingListService.isNameDuplicated$(listName).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['Discover.dialogs.newList.duplicate'];
                            } else {
                                return [];
                            }
                        })
                    );
                }
            },
            outputs: {}
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    /**
     * Converts user ids to usernames for the info tooltip
     */
    private fetchUserNames() {
        const userIds = [...this.dataRows.map(x => +x.originalData.createdByUserId), ...this.dataRows.map(x => +x.originalData.modifiedByUserId)];
        this.subscriptions.push(
            this.usersService.getUsers(userIds).subscribe(users => {
                this.dataRows.forEach(row => {
                    const creator = users.find(x => x.id === row.originalData.createdByUserId);
                    if (creator) {
                        row.createdUser = creator.getDisplayName();
                    }

                    const modifier = users.find(x => x.id === row.originalData.modifiedByUserId);
                    if (modifier) {
                        row.modifiedUser = modifier.getDisplayName();
                    }
                });
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
